import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';

export type RecipeAngelesFieldKey =
  | 'patientName'
  | 'age'
  | 'weight'
  | 'height'
  | 'prescription'
  | 'footerDate'
  | 'footerLine1'
  | 'footerLine2'
  | 'footerLine3'
  | 'doctorLine1'
  | 'doctorLine2'
  | 'doctorLine3'
  | 'qrCode';

export interface FieldPosition {
  top: number;
  left: number;
  width?: number;
}

export interface RecipeAngelesCanvasProps {
  patientName: string;
  ageYears: string;
  ageMonths: string;
  ageDays?: string;
  weight: string;
  weightPercentile: string;
  height: string;
  heightPercentile: string;
  prescription: string;
  consultDate: string;
  recetaToken?: string | null;
  className?: string;
  editMode?: boolean;
  selectedField?: RecipeAngelesFieldKey | null;
  onSelectField?: (field: RecipeAngelesFieldKey) => void;
  fieldPositions?: Partial<Record<RecipeAngelesFieldKey, FieldPosition>>;
  onFieldBoundsChange?: (field: RecipeAngelesFieldKey, size: { width: number; height: number }) => void;
}

export const DEFAULT_ANGELES_POSITIONS: Record<RecipeAngelesFieldKey, FieldPosition> = {
  patientName: { top: 165, left: 250 },
  age: { top: 165, left: 750 },
  weight: { top: 185, left: 250 },
  height: { top: 185, left: 900 },
  prescription: { top: 380, left: 250 },
  footerDate: { top: 2050, left: 100 },
  footerLine1: { top: 2095, left: 100 },
  footerLine2: { top: 2120, left: 100 },
  footerLine3: { top: 2145, left: 100 },
  doctorLine1: { top: 2095, left: 850 },
  doctorLine2: { top: 2120, left: 850 },
  doctorLine3: { top: 2145, left: 850 },
  qrCode: { top: 2095, left: 1300 },
};

export const RECIPE_ANGELES_FIELD_KEYS = Object.keys(DEFAULT_ANGELES_POSITIONS) as RecipeAngelesFieldKey[];

const RecipeAngelesCanvas: React.FC<RecipeAngelesCanvasProps> = ({
  patientName,
  ageYears,
  ageMonths,
  ageDays,
  weight,
  weightPercentile,
  height,
  heightPercentile,
  prescription,
  consultDate,
  recetaToken,
  className = '',
  editMode = false,
  selectedField,
  onSelectField,
  fieldPositions,
  onFieldBoundsChange,
}) => {
  const mergedPositions = useMemo(() => {
    return RECIPE_ANGELES_FIELD_KEYS.reduce<Record<RecipeAngelesFieldKey, FieldPosition>>((acc, key) => {
      const base = DEFAULT_ANGELES_POSITIONS[key];
      const override = fieldPositions?.[key];
      acc[key] = override ? { ...base, ...override } : { ...base };
      return acc;
    }, {} as Record<RecipeAngelesFieldKey, FieldPosition>);
  }, [fieldPositions]);

  const handleSelect = (field: RecipeAngelesFieldKey) => {
    if (editMode) {
      onSelectField?.(field);
    }
  };

  const refs = useMemo(() => {
    return RECIPE_ANGELES_FIELD_KEYS.reduce<Record<RecipeAngelesFieldKey, React.RefObject<HTMLDivElement>>>((acc, key) => {
      acc[key] = React.createRef<HTMLDivElement>();
      return acc;
    }, {} as Record<RecipeAngelesFieldKey, React.RefObject<HTMLDivElement>>);
  }, []);

  useLayoutEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    (Object.keys(refs) as RecipeAngelesFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (el) {
        const rect = el.getBoundingClientRect();
        onFieldBoundsChange(key, { width: rect.width, height: rect.height });
      }
    });
  }, [patientName, ageYears, ageMonths, ageDays, weight, weightPercentile, height, heightPercentile, prescription, consultDate, editMode, refs, onFieldBoundsChange]);

  useEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    const observers: ResizeObserver[] = [];

    (Object.keys(refs) as RecipeAngelesFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (!el) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          onFieldBoundsChange(key, { width, height });
        }
      });

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [refs, onFieldBoundsChange]);

  const classSuffix: Record<RecipeAngelesFieldKey, string> = {
    patientName: 'patient-name',
    age: 'age',
    weight: 'weight',
    height: 'height',
    prescription: 'prescription',
    footerDate: 'footer-date',
    footerLine1: 'footer-line1',
    footerLine2: 'footer-line2',
    footerLine3: 'footer-line3',
    doctorLine1: 'doctor-line1',
    doctorLine2: 'doctor-line2',
    doctorLine3: 'doctor-line3',
    qrCode: 'qr-code',
  };

  const styleFor = (field: RecipeAngelesFieldKey): React.CSSProperties => {
    const pos = mergedPositions[field];
    const base: React.CSSProperties = {
      top: `${pos.top / 100}cm`,
      left: `${pos.left / 100}cm`,
    };
    if (pos.width != null) {
      base.width = `${pos.width / 100}cm`;
    }
    if (editMode) {
      base.cursor = 'move';
    }
    return base;
  };

  const focusableProps = (field: RecipeAngelesFieldKey) => {
    const baseClass = `recipe-field recipe-angeles-field-${classSuffix[field]}`;

    if (editMode) {
      return {
        tabIndex: 0,
        onClick: () => handleSelect(field),
        onFocus: () => handleSelect(field),
        className: `${baseClass} ${selectedField === field ? 'recipe-field-selected' : ''}`.trim(),
      };
    }

    return {
      className: baseClass,
    };
  };

  // Formatear edad - IGUAL QUE BENEFICENCIA
  const ageText = ageDays != null && ageDays !== ''
    ? `${ageYears} años ${ageMonths} meses ${ageDays} días`
    : `${ageYears} años ${ageMonths} meses`;

  return (
    <div
      className={`recipe-print-page recipe-angeles ${editMode ? 'recipe-print-page--edit' : ''} ${className}`.trim()}
    >
      <div ref={refs.patientName} style={styleFor('patientName')} {...focusableProps('patientName')}>
        {patientName}
      </div>
      <div ref={refs.age} style={styleFor('age')} {...focusableProps('age')}>
        {ageText}
      </div>
      <div ref={refs.weight} style={styleFor('weight')} {...focusableProps('weight')}>
        {weight} kg
        {weightPercentile && (
          <div style={{ display: 'block', marginTop: '0.15cm', fontSize: '9.2pt', fontWeight: 700 }}>
            {weightPercentile}
          </div>
        )}
      </div>
      <div ref={refs.height} style={styleFor('height')} {...focusableProps('height')}>
        {height} cm
        {heightPercentile && (
          <div style={{ display: 'block', marginTop: '0.15cm', fontSize: '9.2pt', fontWeight: 700 }}>
            {heightPercentile}
          </div>
        )}
      </div>
      <div ref={refs.prescription} style={styleFor('prescription')} {...focusableProps('prescription')}>
        <span className="whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', tabSize: 38 }}>{prescription}</span>
      </div>
      
      {/* Footer izquierdo */}
      <div ref={refs.footerDate} style={styleFor('footerDate')} {...focusableProps('footerDate')}>
        {consultDate}
      </div>
      <div ref={refs.footerLine1} style={styleFor('footerLine1')} {...focusableProps('footerLine1')}>
        Beneficencia Española Cons. 407 : Lun-mier-vier
      </div>
      <div ref={refs.footerLine2} style={styleFor('footerLine2')} {...focusableProps('footerLine2')}>
        Hospital Angeles Mar-jue-sab
      </div>
      <div ref={refs.footerLine3} style={styleFor('footerLine3')} {...focusableProps('footerLine3')}>
        joseluis@cadena.net
      </div>
      
      {/* Footer derecho */}
      <div ref={refs.doctorLine1} style={{ ...styleFor('doctorLine1'), fontSize: '11pt' }} {...focusableProps('doctorLine1')}>
        DR. JOSE LUIS CADENA S.
      </div>
      <div ref={refs.doctorLine2} style={{ ...styleFor('doctorLine2'), fontSize: '11pt' }} {...focusableProps('doctorLine2')}>
        Pediatra-Cirujano Pediatra
      </div>
      <div ref={refs.doctorLine3} style={{ ...styleFor('doctorLine3'), fontSize: '11pt' }} {...focusableProps('doctorLine3')}>
        22222606190
      </div>

      {/* Código QR */}
      {recetaToken && (
        <div ref={refs.qrCode} style={styleFor('qrCode')} {...focusableProps('qrCode')}>
          <QRCodeSVG
            value={`${window.location.origin}/receta/${recetaToken}`}
            size={45}
            level="M"
            includeMargin={false}
          />
        </div>
      )}
    </div>
  );
};

export default RecipeAngelesCanvas;
